import Modal from "@/components/controls/Modal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { BackArrowLeft, Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { UserData } from "@/entities/users/types";
import CreateCompanyModal from "@/pages/companies/components/CreateCompanyModal";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import CreatePartnerModal from "@/pages/partners/components/CreatePartnerModal";
import CreatePartnerContactModal from "@/pages/partners/contacts/components/CreatePartnerContactModal";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { DealData } from "@/pages/deals/types";
import {
  useCreateDealMutation,
  useResolveDealContractId
} from "@/pages/deals/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import useBoundStore from "@/services/store/BoundStore";
import { mapEmptyToNull } from "@/utils/formatting";
import { useProducts } from "@/entities/settings/hooks";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { excludePartnerFields } from "@/entities/dataTables/helpers";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject,
  getFieldNameByDisplayType
} from "@/utils/entityFieldsUtils/formFields";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { useDealsNavigation } from "@/services/hooks/entities/deal/useDealsNavigation";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { createUnsavedChangesModalSetter } from "@/entities/modals/helpers";
import Stepper from "@/components/ui/stepper";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useCreateRevenueItem } from "@/pages/contracts/hooks";
import DealServicesSection from "@/pages/deals/components/CreateDealServices/DealServicesSection";
import DealFinancialsFooter from "@/pages/deals/components/CreateDealServices/DealFinancialsFooter";
import { useDealServiceLines } from "@/pages/deals/components/CreateDealServices/useDealServiceLines";
import {
  buildWizardSections,
  DEAL_DETAILS_SECTION_ID,
  PRODUCT_SERVICES_SECTION_ID
} from "@/pages/deals/components/CreateDealServices/wizardSections";
import { useCurrencyConversion } from "@/services/hooks/useCurrencyConversion";
import { useArrMrr } from "@/services/context/ArrMrrContext";

type WatchAllFields = Parameters<
  React.ComponentProps<typeof FormNew>["isFormValid"]
>[1];

// Carries the names of the services that failed so the toast can list them.
class PartialServicesError extends Error {}

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userData: UserData;
  onDealCreated?: () => void;
  companyId?: string;
  personId?: string;
  partnerId?: string;
}

const CreateDealModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  userData,
  onDealCreated,
  companyId,
  personId,
  partnerId
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const createDeal = useCreateDealMutation();
  const resolveDealContractId = useResolveDealContractId();
  // carryOverServices names the failed services in one toast of its own.
  const createRevenueItem = useCreateRevenueItem({ toastOnError: false });
  const { navigateToDetailedView } = useDealsNavigation();
  const { enabled: isProductServicesFlagEnabled } = useFeatureFlag(
    FeatureFlags.CRM_8742_DEAL_PRODUCT_SERVICES
  );
  const { fieldName: recurringRevenueFieldName } = useArrMrr();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [manualRecurringRevenue, setManualRecurringRevenue] = useState("");

  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [formGetValues, setFormGetValues] = useState<any>(null);
  const [view, setView] = useState<
    "deals" | "companies" | "people" | "partners" | "partnerContacts"
  >("deals");
  const [isFormValid, setIsFormValid] = useState(false);
  // FormNew validates the visible section only, so step one's verdict is kept.
  const [isDealDetailsValid, setIsDealDetailsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");
  const [hasUserEnteredDealName, setHasUserEnteredDealName] = useState(false);
  const [currentProductId, setCurrentProductId] = useState<string>("");
  // A partner makes company optional; partner contacts make the contact optional.
  const [hasPartner, setHasPartner] = useState(false);
  const [hasPartnerContacts, setHasPartnerContacts] = useState(false);

  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);
  const [fieldResetKeys, setFieldResetKeys] = useState<Record<string, number>>(
    {}
  );

  const products = useProducts().data?.products ?? [];
  const { data: stagesResponse } = useDealStages();

  const { data: entityFieldsResponse } = useEntityFields(EntityType.DEAL);
  const updateEntityField = useUpdateEntityField(EntityType.DEAL);
  const enablePartnerships = useEnablePartnerships();
  const visibleFields = useMemo(
    () =>
      enablePartnerships
        ? entityFieldsResponse?.fields
        : excludePartnerFields(entityFieldsResponse?.fields ?? []),
    [entityFieldsResponse?.fields, enablePartnerships]
  );

  const users = useBoundStore((state) => state.users.entities);
  const getCompany = useBoundStore((state) => state.companies.getEntity);

  const getCompanyName = (companyId: string): string | null => {
    const company = getCompany(companyId);
    return company?.name || null;
  };
  const getProductName = (productId: string): string | null => {
    const product = products.find((p) => p.id === productId);
    return product?.name || null;
  };

  const autoPopulateDealName = (
    companyName: string,
    currentProductId?: string
  ) => {
    if (!hasUserEnteredDealName && typeof formSetValue === "function") {
      let dealName = companyName;

      if (currentProductId) {
        const selectedProduct = products.find(
          (product) => product.id === currentProductId
        );
        if (selectedProduct) {
          dealName = `${companyName} ${selectedProduct.name}`;
        }
      }

      formSetValue("name", dealName, {
        shouldValidate: true
      });
    }
  };

  const onCompanySelect = (companyId: string) => {
    setSelectedCompanyId(companyId);
    const companyName = getCompanyName(companyId);
    if (companyName) {
      autoPopulateDealName(companyName, currentProductId || undefined);
    }
  };

  const onProductSelect = (productId: string) => {
    setCurrentProductId(productId);
    const productName = getProductName(productId);
    if (productName && selectedCompanyId) {
      const companyName = getCompanyName(selectedCompanyId);
      if (companyName) {
        autoPopulateDealName(companyName, productId);
      }
    }
  };

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: "deals",
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      if (formSetValue && typeof formGetValues === "function") {
        const isMultiSelect = currentField.displayType === "multiSelect";
        const existingValues =
          formGetValues()[currentFieldForNewOption.fieldKey];
        const safeArray = Array.isArray(existingValues) ? existingValues : [];
        const updatedSelection = isMultiSelect
          ? [...safeArray, optionName]
          : optionName;

        formSetValue(currentFieldForNewOption.fieldKey, updatedSelection, {
          shouldValidate: true
        });

        // Only reset non-multiSelect fields to prevent losing selections
        if (!isMultiSelect) {
          setTimeout(() => {
            setFieldResetKeys((prev) => ({
              ...prev,
              [currentFieldForNewOption.fieldKey]:
                (prev[currentFieldForNewOption.fieldKey] || 0) + 1
            }));
          }, 100);
        }
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData,
    selectedCompanyId,
    selectedPersonId,
    setSelectedCompanyId,
    setSelectedPersonId,
    setHasUserEnteredDealName,
    autoPopulateDealName,
    setView,
    hasUserEnteredDealName,
    onCompanySelect,
    onProductSelect,
    entityType: EntityType.DEAL,
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    fieldResetKeys,
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption
  };

  const baseSections = createFormNewSectionsFromAPI(
    visibleFields,
    t,
    formFieldsContext
  );

  const partnerFieldName = getFieldNameByDisplayType(
    baseSections,
    FieldDisplayType.PARTNERS
  );
  const partnerContactsFieldName = getFieldNameByDisplayType(
    baseSections,
    FieldDisplayType.PARTNER_CONTACTS
  );

  const productFieldName = getFieldNameByDisplayType(
    baseSections,
    FieldDisplayType.PRODUCTS
  );
  // A plain select rather than an entity field, so there is no displayType.
  const currencyFieldName = baseSections
    .flatMap((section) => section.fields ?? [])
    .find((field) => field.type === "currencySelect")?.name;

  // No Product field means no catalog, so those accounts keep the single step.
  const isProductServicesWizard =
    isProductServicesFlagEnabled && !!productFieldName;

  const decoratedSections = baseSections.map((section) => ({
    ...section,
    fields: section.fields?.map((field) => ({
      ...field,
      labelPosition: "left" as const,
      ...(field.name === "companyId" && hasPartner ? { required: false } : {}),
      ...(field.name === "contactId" && (hasPartner || hasPartnerContacts)
        ? { required: false }
        : {})
    }))
  }));

  const formSections: FormSectionProps[] = useMemo(() => {
    if (!isProductServicesWizard) return decoratedSections;

    return buildWizardSections(
      decoratedSections,
      [productFieldName, currencyFieldName].filter(
        (name): name is string => !!name
      )
    );
  }, [
    decoratedSections,
    isProductServicesWizard,
    productFieldName,
    currencyFieldName
  ]);

  const isProductServicesActive = isProductServicesWizard && activeStep === 1;
  const { accountCurrency } = useCurrencyConversion(selectedCurrency);
  const currency = selectedCurrency || accountCurrency;
  const serviceLines = useDealServiceLines(currentProductId);
  const { reset: resetServiceLines } = serviceLines;

  const wizardSteps = useMemo(
    () => [
      {
        id: DEAL_DETAILS_SECTION_ID,
        title: t("deals.createModal.steps.dealDetails"),
        counterLabel: t("stepperStepCounter", { number: 1 })
      },
      {
        id: PRODUCT_SERVICES_SECTION_ID,
        title: t("deals.createModal.steps.productAndServices"),
        counterLabel: t("stepperStepCounter", { number: 2 }),
        disabled: !isDealDetailsValid
      }
    ],
    [t, isDealDetailsValid]
  );

  // The active step decides what FormNew renders and therefore what it validates.
  const activeSectionIds = useMemo(
    () =>
      isProductServicesWizard
        ? [
            activeStep === 0
              ? DEAL_DETAILS_SECTION_ID
              : PRODUCT_SERVICES_SECTION_ID
          ]
        : undefined,
    [isProductServicesWizard, activeStep]
  );

  // A catalog with no services offers nothing to pick, so ARR is entered by hand.
  const needsManualRecurringRevenue =
    isProductServicesWizard &&
    !!currentProductId &&
    !serviceLines.isLoadingProducts &&
    !serviceLines.hasAvailableServices;

  const canSubmit =
    isFormValid &&
    !isSubmitting &&
    (!isProductServicesWizard ||
      (isDealDetailsValid &&
        (needsManualRecurringRevenue
          ? Number(manualRecurringRevenue) > 0
          : serviceLines.areLinesValid)));

  // Called on every render, so each setter compares first to avoid a loop.
  const syncFieldDerivedState = (watchAllFields: WatchAllFields) => {
    const partnerChosen = !!(
      partnerFieldName && watchAllFields?.[partnerFieldName]
    );
    const partnerContactsChosen = !!(
      partnerContactsFieldName && watchAllFields?.[partnerContactsFieldName]
    );
    const chosenCurrency = currencyFieldName
      ? (watchAllFields?.[currencyFieldName] ?? "")
      : "";
    setHasPartner((prev) => (prev === partnerChosen ? prev : partnerChosen));
    setHasPartnerContacts((prev) =>
      prev === partnerContactsChosen ? prev : partnerContactsChosen
    );
    setSelectedCurrency((prev) =>
      prev === chosenCurrency ? prev : chosenCurrency
    );
  };

  const { handleCloseAttempt, warningDialogProps } = useUnsavedChanges(
    () => formGetValues?.() || {},
    formSections
  );

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    } else {
      formRef.current?.reset();
      setSelectedCompanyId("");
      setSelectedPersonId("");
      setCurrentProductId("");
      setView("deals");
      setHasUserEnteredDealName(false);
      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
      setFieldResetKeys({});
      setIsFormValid(false);
      setIsDealDetailsValid(false);
      setHasPartner(false);
      setHasPartnerContacts(false);
      setActiveStep(0);
      setSelectedCurrency("");
      setManualRecurringRevenue("");
    }
  }, [isOpen]);

  // A product switch invalidates the services added from the previous catalog.
  useEffect(() => {
    resetServiceLines();
    setManualRecurringRevenue("");
  }, [currentProductId, resetServiceLines]);

  useEffect(() => {
    if (companyId && typeof formSetValue === "function") {
      formSetValue("companyId", companyId, {
        shouldValidate: true
      });
      setSelectedCompanyId(companyId);

      const companyName = getCompanyName(companyId);
      if (companyName) {
        autoPopulateDealName(companyName, currentProductId || undefined);
      }
    }
    if (personId && typeof formSetValue === "function") {
      formSetValue("contactId", personId, {
        shouldValidate: true
      });
      setSelectedPersonId(personId);
    }
  }, [companyId, personId, formSetValue, currentProductId]);

  useEffect(() => {
    if (partnerId && partnerFieldName && typeof formSetValue === "function") {
      formSetValue(partnerFieldName, partnerId, { shouldValidate: true });
    }
  }, [partnerId, partnerFieldName, formSetValue]);

  useEffect(() => {
    if (
      isOpen &&
      products?.length === 1 &&
      products[0]?.id &&
      typeof formSetValue === "function" &&
      typeof formGetValues === "function"
    ) {
      const singleProductId = products[0].id;
      const currentFormProductId = formGetValues()?.productId;

      if (!currentFormProductId) {
        setCurrentProductId(singleProductId);

        formSetValue("productId", singleProductId, {
          shouldValidate: true
        });

        // If a company is already selected, trigger deal name auto-population
        if (selectedCompanyId) {
          const companyName = getCompanyName(selectedCompanyId);
          if (companyName) {
            autoPopulateDealName(companyName, singleProductId);
          }
        }
      }
    }
  }, [isOpen, products, formSetValue, formGetValues, selectedCompanyId]);

  // Carries the services into the new deal; a failure leaves the deal in place.
  const carryOverServices = async (dealId: string, data: DealData) => {
    const revenueItems = serviceLines.buildRevenueItemRequests(
      data.expectedCloseAt ?? null
    );
    if (revenueItems.length === 0) return;

    // The services attach to the contract dealsapi already paired with the deal.
    // Creating another one here would leave the deal with two live contracts,
    // which breaks the Closed Won hand-off in contractsapi.
    const contractId = await resolveDealContractId(dealId);
    if (!contractId) throw new Error("Deal has no paired contract");

    // One request each, so attempt them all and name the ones to re-add.
    const failedServices: string[] = [];
    for (const revenueItem of revenueItems) {
      try {
        await createRevenueItem.mutateAsync({
          pathParams: { contractId },
          data: revenueItem
        });
      } catch {
        failedServices.push(revenueItem.serviceItemName);
      }
    }
    if (failedServices.length > 0) {
      throw new PartialServicesError(failedServices.join(", "));
    }
  };

  const handleSubmit = async (data: DealData) => {
    setIsSubmitting(true);
    const stages = stagesResponse?.stages ?? [];
    const firstStageId = stages.length > 0 ? stages[0].id : 1;

    const transformFormData: DealData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data)),
      ...(data.arr !== undefined && {
        arr: data.arr ? Number(data.arr) : null
      }),
      ...(data.mrr !== undefined && {
        mrr: data.mrr ? Number(data.mrr) : null
      }),
      // The wizard drops the field, so the hand-entered value is added back here.
      ...(needsManualRecurringRevenue && {
        [recurringRevenueFieldName]: manualRecurringRevenue
          ? Number(manualRecurringRevenue)
          : null
      }),
      status: 1,
      stage: firstStageId,
      manuallyAddedPeople: data.contactId ? [data.contactId] : []
    };
    await createDeal.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        if (isProductServicesWizard && response?.id) {
          try {
            await carryOverServices(response.id, transformFormData);
          } catch (error) {
            toast.error(
              <ToastBody
                title={
                  error instanceof PartialServicesError
                    ? t("deals.createModal.servicesPartialError", {
                        services: error.message
                      })
                    : t("deals.createModal.servicesError")
                }
              />
            );
          }
        }
        toast.success(
          <ToastBody
            title={`${data.name} ${t("deals.createModal.success")}`}
            actions={
              <Button
                className="size-fit p-1"
                onClick={() => {
                  toast.dismiss();
                  navigateToDetailedView(response?.id);
                }}
                variant="primary"
              >
                {t("deals.createModal.view")}
              </Button>
            }
          />,
          {
            duration: 5000
          }
        );
        onDealCreated?.();
        setIsOpen(false);
      },
      onError: (_error: Error) => {
        setIsSubmitting(false);
        toast.error(
          <ToastBody
            title={t("deals.createModal.error")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    });
  };

  const handleCancel = () => {
    handleCloseAttempt(() => {
      setIsOpen(false);
    });
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  const onCompanyCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedCompanyId(id);
    if (typeof formSetValue === "function") {
      formSetValue("companyId", id, {
        shouldValidate: true
      });

      autoPopulateDealName(name);
    }
  };

  const onPersonCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedPersonId(id);
    if (typeof formSetValue === "function") {
      formSetValue("contactId", id, {
        shouldValidate: true
      });
    }
  };

  const onPartnerCreation = (id: string, name: string) => {
    if (!id || !name || !partnerFieldName) return;
    if (typeof formSetValue === "function") {
      formSetValue(partnerFieldName, id, { shouldValidate: true });
    }
  };

  const onPartnerContactCreation = (id: string, name: string) => {
    if (!id || !name || !partnerContactsFieldName) return;
    if (typeof formSetValue === "function") {
      formSetValue(partnerContactsFieldName, id, { shouldValidate: true });
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={createUnsavedChangesModalSetter(
          setIsOpen,
          handleCloseAttempt
        )}
        data-test="deals-create-modal"
        title={
          <Text
            as="div"
            variant="lgSubtitleH1"
            data-test="deals-create-modal-title"
          >
            {t("deals.createModal.title")}
          </Text>
        }
        content={
          <div
            className={
              isProductServicesWizard
                ? "flex h-[60vh]"
                : "h-[60vh] overflow-y-auto"
            }
            data-test="deals-create-modal-content"
          >
            {isProductServicesWizard ? (
              <div className="w-[264px] shrink-0 border-r-[0.5px] border-border-border1 py-4">
                <Stepper
                  steps={wizardSteps}
                  activeStep={activeStep}
                  orientation="vertical"
                  showStepCounter={false}
                  onStepClick={(index) => setActiveStep(index)}
                />
              </div>
            ) : null}
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-6">
                <FormNew
                  sections={formSections}
                  visibleSectionIds={activeSectionIds}
                  isFormValid={(isValid, watchAllFields) => {
                    setIsFormValid(isValid);
                    if (!isProductServicesWizard || activeStep === 0) {
                      setIsDealDetailsValid((prev) =>
                        prev === isValid ? prev : isValid
                      );
                    }
                    syncFieldDerivedState(watchAllFields);
                  }}
                  onSubmit={handleSubmit}
                  formRef={formRef}
                  onSetValue={handleSetValue}
                />
                {isProductServicesActive ? (
                  <DealServicesSection
                    state={serviceLines}
                    productId={currentProductId}
                    currency={currency}
                    needsManualRecurringRevenue={needsManualRecurringRevenue}
                    manualRecurringRevenue={manualRecurringRevenue}
                    onManualRecurringRevenueChange={setManualRecurringRevenue}
                  />
                ) : null}
              </div>
              {isProductServicesActive ? (
                <DealFinancialsFooter
                  estimate={serviceLines.estimate}
                  currency={currency}
                />
              ) : null}
            </div>
          </div>
        }
        footer={
          <div
            className="flex items-center justify-between gap-2"
            data-test="deals-create-modal-footer"
          >
            {isProductServicesActive ? (
              <Button
                data-test="deals-create-modal-back-button"
                variant="tertiary"
                onClick={() => setActiveStep(0)}
              >
                <BackArrowLeft />
                {t("back")}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex justify-end gap-2">
              <Button
                data-test="deals-create-modal-cancel-button"
                variant="secondary"
                onClick={handleCancel}
              >
                {t("cancel")}
              </Button>
              {isProductServicesWizard && activeStep === 0 ? (
                <Button
                  data-test="deals-create-modal-continue-button"
                  variant="primary"
                  disabled={!isFormValid}
                  onClick={() => setActiveStep(1)}
                >
                  {t("deals.createModal.continue")}
                </Button>
              ) : (
                <Button
                  data-test="deals-create-modal-submit-button"
                  variant="primary"
                  disabled={!canSubmit}
                  onClick={handleCreateNew}
                >
                  {t("deals.createModal.createNew")}
                </Button>
              )}
            </div>
          </div>
        }
        contentContainerClassname={
          isProductServicesWizard
            ? "w-[1024px] max-w-[calc(100vw-4rem)]"
            : "w-[640px]"
        }
      />
      <CreateCompanyModal
        isOpen={view === "companies"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("deals");
          }
        }}
        onSuccess={onCompanyCreation}
      />
      <CreatePersonModal
        isOpen={view === "people"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("deals");
          }
        }}
        companyId={selectedCompanyId}
        onSuccess={onPersonCreation}
      />
      <CreatePartnerModal
        isOpen={view === "partners"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("deals");
          }
        }}
        onSuccess={onPartnerCreation}
      />
      <CreatePartnerContactModal
        isOpen={view === "partnerContacts"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("deals");
          }
        }}
        onSuccess={onPartnerContactCreation}
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => {
          setShowCreateOptionModal(false);
        }}
        onCreateOption={(optionName) => {
          return handleCreateNewOption(optionName);
        }}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
      />
      <UnsavedChangesWarningDialog {...warningDialogProps} />
    </>
  );
};

export default CreateDealModal;
